/**
 * Shared Hebrew labels and small presentational helpers for the NiV telemetry
 * surfaces (the analytics tab, the clinic detail tab and the clinics drawer).
 *
 * These live here rather than in NivTelemetryTab so that gutting or
 * restructuring that tab never breaks its importers.
 */

import { CaretUp, CaretDown } from "@phosphor-icons/react";

export const PAGE_LABELS = {
  home: "דף הבית",
  calendar: "יומן ותורים",
  patients: "מטופלים",
  patientProfile: "כרטיס מטופל",
  treatmentDoc: "תיעוד טיפול",
  finance: "פיננסים",
  financeReports: "דוחות פיננסיים",
  reports: "דוח חודשי",
  tasks: "ארגז הכלים",
  settings: "הגדרות",
  support: "תמיכה",
  liveSession: "מפגש חי",
  adminSnapshot: "תמונת מצב",
  adminOverview: "סקירת אדמין",
  adminPatients: "מטופלים (אדמין)",
  adminCalendar: "יומנים (אדמין)",
  adminTasks: "משימות (אדמין)",
  adminActivityLogs: "לוגי פעילות",
  adminSettings: "הגדרות קליניקה",
};

export const FEATURE_LABELS = {
  rooms: "ניהול חדרים",
  googleCalendarSync: "סנכרון Google",
  financeActions: "פעולות פיננסיות",
  morningInvoice: "חשבוניות Morning",
  digitalInvoice: "חשבוניות דיגיטליות",
  monthlyReports: "דוח חודשי",
  questionnaires: "שאלונים",
  digitalSignature: "חתימה דיגיטלית",
  liveSession: "מפגש חי",
  games: "משחקים",
  videoMeetings: "פגישות וידאו",
};

// featureStatus.state (written by the nightly usage run) → Hebrew chip
export const FEATURE_STATE_LABELS = {
  active: "פעיל",
  recent: "לאחרונה",
  connected_idle: "מחובר, ללא שימוש",
  stale: "לא בשימוש",
  never: "מעולם לא",
  not_applicable: "לא רלוונטי",
};

export const featureStateLabel = (state) => FEATURE_STATE_LABELS[state] || "—";

export const DEVICE_LABELS = {
  desktop: "מחשב",
  tablet: "טאבלט",
  mobile: "נייד",
};

export const pageLabel = (key) => PAGE_LABELS[key] || key;
export const featureLabel = (key) => FEATURE_LABELS[key] || key;

/** "computedAt" (ISO / serialized Timestamp) → "9.9 14:32" in Israel time */
export const formatComputedAt = (value) => {
  if (!value) return null;
  const d = new Date(value?.seconds ? value.seconds * 1000 : value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("he-IL", {
    timeZone: "Asia/Jerusalem",
    day: "numeric",
    month: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const TrendArrow = ({ value }) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) {
    return <span className="ntl-trend ntl-trend-flat">—</span>;
  }
  if (num > 0) {
    return (
      <span className="ntl-trend ntl-trend-up">
        <CaretUp size={11} weight="fill" />
        {Math.abs(Math.round(num))}
      </span>
    );
  }
  return (
    <span className="ntl-trend ntl-trend-down">
      <CaretDown size={11} weight="fill" />
      {Math.abs(Math.round(num))}
    </span>
  );
};

/** Tone for the "waiting for update" backlog badge (mirrors push thresholds 5/10). */
export const pendingUpdateTone = (count) => {
  const n = Number(count) || 0;
  if (n >= 10) return "danger";
  if (n >= 5) return "warn";
  return "ok";
};
